import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { ButtonHTMLAttributes, ForwardedRef, forwardRef } from "react";
import { BsArrowClockwise } from "react-icons/bs";

import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-3xl text-sm font-medium transition-colors outline-none focus-visible:outline-primary-300 disabled:cursor-not-allowed disabled:opacity-70",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-primary hover:bg-gradient-primary-hover disabled:bg-gradient-primary text-primary-foreground shadow-md shadow-gray-400",
        secondary: "bg-secondary-200 hover:bg-secondary-300 disabled:bg-secondary-200 active:bg-secondary-400 text-secondary-foreground",
        tertiary: "bg-primary-100 hover:bg-primary-300 text-black hover:text-white",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/70 disabled:bg-destructive border border-foreground",
        outline:
          "bg-white text-primary-400 border border-primary-400 hover:bg-gray-200 active:bg-gray-300 hover:border-black hover:text-black disabled:bg-white shadow-md shadow-gray-400",
        ghost: "rounded-md border border-transparent hover:bg-gray-200 hover:border-gray-600 disabled:bg-transparent",
        dark: "text-white bg-neutral-400 hover:bg-neutral-500 active:bg-black",
        plain: "rounded-none",
      },
      size: {
        default: "h-9 px-4",
        sm: "h-7 px-2 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
  };

function ForwardedButton(props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) {
  const { children, isLoading, disabled, className, variant, size, type = "button", asChild = false, ...rest } = props;
  const Component = asChild ? Slot : "button";

  return (
    <Component
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled ?? isLoading}
      {...rest}
    >
      {asChild ? (
        children
      ) : (
        <>
          {isLoading && <BsArrowClockwise className="w-6 h-6 animate-spin" />}
          {children}
        </>
      )}
    </Component>
  );
}

export const Button = forwardRef(ForwardedButton);
